import {
  IAccount,
  IAccountService,
  IPropertySelection,
  IService,
  IUserContext,
} from "@origin-digital/event-dispatcher";
import { getFirst, groupBy, isPresent } from "./utils";

const isClosedService = (service: IService): boolean =>
  service.serviceStatus === "CLOSED";

const mapAccountServices = (accounts: IAccount[]): IAccountService[] => {
  const result: IAccountService[] = [];
  accounts.forEach((account) => {
    account.services.forEach((service) => {
      result.push({
        accountId: account.backendAccountId,
        serviceId: service.backendServiceId,
        serviceType: service.productType,
        isClosed: isClosedService(service),
      });
    });
  });
  return result;
};

const mapPropertyGroupsToProperties = (
  propertyGroups: Record<string, IAccount[]>
): IPropertySelection[] => {
  return Object.values(propertyGroups)
    .map((accountGroup) => {
      const property = getFirst(accountGroup[0].services)?.property;
      if (!property) {
        return undefined;
      }
      const accountServices: IAccountService[] =
        mapAccountServices(accountGroup);
      const isPrevious = accountServices.every(
        (accountService) => accountService.isClosed
      );
      return {
        ...property,
        accountServices,
        isPrevious,
      };
    })
    .filter(isPresent);
};

/**
 * @param userContext Data from the user context service.
 * @returns 2 sets of property groupings, the first containing properties with an open service, and the second containing properties with all services closed.
 *          Each property grouping represents a single property, and contains a collection of all account-service combinations for that property.
 */
export const getAllPropertiesForUser = (
  userContext?: IUserContext
): [IPropertySelection[], IPropertySelection[]] => {
  if (!userContext) {
    return [[], []];
  }

  const propertyGroups = groupBy<IAccount>(
    userContext.accounts,
    (account) => getFirst(account.services)?.property.id
  );

  const properties: IPropertySelection[] =
    mapPropertyGroupsToProperties(propertyGroups);

  const result: [IPropertySelection[], IPropertySelection[]] = [[], []];
  properties.forEach((property) => {
    if (property.isPrevious) {
      result[1].push(property);
    } else {
      result[0].push(property);
    }
  });
  return result;
};
